import { useState } from 'react';
import { apiDelete, apiPostJson, imageUrl, thumbUrl } from '../api.js';
import { CollabField } from '../editor/CollabField.jsx';
import { ImageEditDialog } from './ImageEditDialog.jsx';
import { EntityImagePickerModal } from './EntityImagePickerModal.jsx';

export function ImageGallery({
  images,
  mainImageId,
  wardrobeImageId = null,
  wardrobePath = null,
  onChange,
  uploadPath,
  deletePath,
  mainPath,
  editPath,
  generatePath,
  characterSourcesPath,
  beatSourcesPath,
  setSourcesPath,
  copyPath,
  referenceHost,
  pickerTitle = 'Add image',
  // Optional controlled-mode props. When `pickerOpen` is supplied the parent
  // owns the open/close state; otherwise the gallery manages it locally.
  pickerOpen: pickerOpenProp,
  onPickerOpenChange,
  hideAddButton = false,
}) {
  const [error, setError] = useState(null);
  const [editingImageId, setEditingImageId] = useState(null);
  const [regenBusyId, setRegenBusyId] = useState(null);
  const [internalPickerOpen, setInternalPickerOpen] = useState(false);
  const pickerOpen = pickerOpenProp ?? internalPickerOpen;
  const setPickerOpen = onPickerOpenChange ?? setInternalPickerOpen;

  async function setMain(id) {
    if (!mainPath) return;
    try {
      await apiPostJson(mainPath, { image_id: id });
      await onChange?.();
    } catch (e) {
      setError(e.message);
    }
  }

  async function setWardrobe(id) {
    if (!wardrobePath) return;
    try {
      await apiPostJson(wardrobePath, { image_id: id || null });
      await onChange?.();
    } catch (e) {
      setError(e.message);
    }
  }

  async function remove(id) {
    if (!deletePath) return;
    if (!confirm('Remove this image?')) return;
    try {
      await apiDelete(deletePath(id));
      await onChange?.();
    } catch (e) {
      setError(e.message);
    }
  }

  async function submitEdit({ mode, imageModel, prompt }) {
    if (!editingImageId) return;
    const id = editingImageId;
    setEditingImageId(null);
    setRegenBusyId(id);
    setError(null);
    try {
      if (mode === 'variant') {
        // Add a brand-new gallery image, anchored on the existing one (passed
        // as a reference) rather than replacing it.
        if (!generatePath) throw new Error('Variant generation is not available here.');
        await apiPostJson(generatePath, {
          prompt,
          model: imageModel,
          reference_image_ids: [id],
        });
      } else {
        // Edit-in-place: regenerate replaces the existing image in its slot.
        if (!editPath) throw new Error('Editing is not available here.');
        await apiPostJson(editPath(id), {
          mode,
          image_model: imageModel,
          prompt,
        });
      }
      await onChange?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setRegenBusyId(null);
    }
  }

  const mainId = mainImageId?.toString?.() || (typeof mainImageId === 'string' ? mainImageId : null);
  const wardrobeId = wardrobeImageId?.toString?.() || (typeof wardrobeImageId === 'string' ? wardrobeImageId : null);

  return (
    <div>
      {error && <div className="error-banner">{error}</div>}
      <div className="image-gallery-list" style={{ marginBottom: 8 }}>
        {(images || []).map((img) => {
          const id = img._id.toString ? img._id.toString() : String(img._id);
          const isMain = mainId && id === mainId;
          const isWardrobe = wardrobeId && id === wardrobeId;
          const regenBusy = regenBusyId === id;
          return (
            <div key={id} className={`gallery-row${isMain ? ' is-main' : ''}`}>
              <div className="gallery-thumb">
                <a
                  href={imageUrl(id)}
                  target="_blank"
                  rel="noreferrer"
                  title="Open full size in new tab"
                >
                  <img src={thumbUrl(id)} alt={img.filename || ''} loading="lazy" />
                </a>
              </div>
              <div className="gallery-meta">
                <CollabField
                  field={`image:${id}:name`}
                  placeholder="Untitled"
                />
                <CollabField
                  field={`image:${id}:description`}
                  multiline
                  placeholder="Description…"
                />
              </div>
              <div className="gallery-actions">
                {isMain ? (
                  <span style={{ color: 'var(--accent)', fontSize: 12 }}>★ main</span>
                ) : (
                  mainPath && <button onClick={() => setMain(id)}>Set main</button>
                )}
                {wardrobePath && (isWardrobe ? (
                  <button className="is-wardrobe-badge" onClick={() => setWardrobe(null)} title="This is the wardrobe plate — every render copies its clothes. Click to clear.">👔 wardrobe</button>
                ) : (
                  <button onClick={() => setWardrobe(id)} title="Make this the wardrobe plate: every render of this character copies its clothes">Set wardrobe</button>
                ))}
                <a
                  className="icon-link"
                  href={imageUrl(id)}
                  download={img.filename || `image-${id}`}
                  title={`Download ${img.filename || 'image'}`}
                >
                  Download
                </a>
                {editPath && (
                  <button
                    onClick={() => setEditingImageId(id)}
                    disabled={regenBusy}
                    title="Edit or regenerate this image with an AI model"
                  >
                    {regenBusy ? 'Editing…' : 'Edit…'}
                  </button>
                )}
                {deletePath && <button onClick={() => remove(id)}>Delete</button>}
              </div>
            </div>
          );
        })}
      </div>
      {uploadPath && !hideAddButton && (
        <div className="gallery-add">
          <button
            type="button"
            className="primary"
            onClick={() => setPickerOpen(true)}
          >
            + Add image
          </button>
        </div>
      )}
      {editPath && (
        <ImageEditDialog
          open={!!editingImageId}
          onClose={() => setEditingImageId(null)}
          onSubmit={submitEdit}
        />
      )}
      {uploadPath && (
        <EntityImagePickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          title={pickerTitle}
          uploadPath={uploadPath}
          generatePath={generatePath || null}
          characterSourcesPath={characterSourcesPath || null}
          beatSourcesPath={beatSourcesPath || null}
          setSourcesPath={setSourcesPath || null}
          copyPath={copyPath || null}
          referenceHost={referenceHost || null}
          onAttached={onChange}
        />
      )}
    </div>
  );
}
